import { useEffect, useState } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import Calendar from "./calendar";
import TimeComponent from "./time-component";
import cargandoCard from "./cargando-card";
import { FORMULARIO_VIAJE_KEY } from "../utils/const/formulario-const";
import { COLOR_AZUL_MAIN } from "../utils/const/colors-const";
import { ESTILO_BOTON_MODAL_ACEPTAR } from "../utils/const/estilos-const";
import { obtenerListaTransportadora } from "../api/api-transportadora";
import { obtenerTodasLasUnidades } from "../api/gestion-dts/gestion";
import {
    obtenerGeocercasPorIdGrupoGeocerca,
    obtenerListaGrupoGeocerca,
} from "../api/api-grupo-geocerca";
import { obtenerListaProducto } from "../api/api-producto";
import { obtenerListaTipoCarguio } from "../api/api-tipo-carguio";
import {
    obtenerUsuarioMovilTipoChofer,
    obtenerInfoConductorConNumeroPlaca,
} from "../api/api-usuario-movil";
import { actualizarProgramacionCamiones } from "../api/api-programacion-camiones";
import { useToast, useProcessing } from "../context/ui-feedback-context";
import { TIPO_INPUT, useTipoInputOptions } from "../page/viaje/tipo_input";
import { sharedIsValidDecimalNumber } from "../utils/shared-functions";
import MetaTemplatePicker from "./meta-template-picker";

const FLAGS_SEMANTICOS_META = [
    "geocerca_origen",
    "geocerca_destino",
    "fecha_inicio_viaje",
    "fecha_fin_viaje",
    "hora_inicio_viaje",
    "hora_fin_viaje",
    "georuta",
    "vehiculo_viaje",
];

const FORM_BG = "#e8f1f8";
const FORM_BORDER = "#dadce0";
const FORM_TEXT = "#0F3457";
const FORM_REQUIRED = "#d93025";

const META_LABEL_MAX_LENGTH = 512;
const META_TEXTO_MAX_LENGTH = 1024;
const META_NOTA_MAX_LENGTH = 2048;
const META_NUMERO_MAX_LENGTH = 10;
const META_NUMERO_MAX_DECIMALS = 4;
const META_LABEL_ALLOWED_REGEX = /^[a-zA-Z0-9]*$/;
const META_LABEL_SANITIZE_REGEX = /[^a-zA-Z0-9]/g;

const parseRespuestas = (item) => {
    if (Array.isArray(item?.respuestasParsed)) return item.respuestasParsed;
    try {
        if (typeof item?.respuestas === "string" && item.respuestas.trim()) {
            const parsed = JSON.parse(item.respuestas);
            return Array.isArray(parsed) ? parsed : [];
        }
        if (Array.isArray(item?.respuestas)) return item.respuestas;
    } catch (err) {
        console.log("DetalleRequerimientoViajeComponent parseRespuestas err", err);
    }
    return [];
};

const parseMeta = (item) => {
    try {
        if (Array.isArray(item?.meta)) return item.meta;
        if (typeof item?.meta === "string" && item.meta.trim()) {
            const parsed = JSON.parse(item.meta);
            return Array.isArray(parsed) ? parsed : [];
        }
    } catch (err) {
        console.log("DetalleRequerimientoViajeComponent parseMeta err", err);
    }
    return [];
};

const createMetaFieldId = () =>
    `meta-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;


/**
 * Editable detalle de una solicitud/programacion.
 * Recibe `item` (con respuestas) y permite editar/guardar como programacion-camiones step 2.
 */
export default function DetalleRequerimientoViajeComponent({
    item = null,
    onSaved,
}) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const { showProcessing, hideProcessing } = useProcessing();

    const [cargandoCatalogos, setCargandoCatalogos] = useState(false);
    const [dynamicFields, setDynamicFields] = useState([]);
    const [metaFields, setMetaFields] = useState([]);
    const [tipoInputSelected, setTipoInputSelected] = useState(null);
    const [metaValidationVisible, setMetaValidationVisible] = useState(false);

    const [listaTransportadoras, setListaTransportadoras] = useState([]);
    const [listaVehiculos, setListaVehiculos] = useState([]);
    const [listaGeocercaOrigenes, setListaGeocercaOrigenes] = useState([]);
    const [listaGeocercaDestinos, setListaGeocercaDestinos] = useState([]);
    const [listaGeocercaGeoruta, setListaGeocercaGeoruta] = useState([]);
    const [listaProductos, setListaProductos] = useState([]);
    const [listaTiposCarga, setListaTiposCarga] = useState([]);
    const [listaChoferes, setListaChoferes] = useState([]);

    const listaTipoInput = useTipoInputOptions();

    useEffect(() => {
        if (item !== null) {
            // console.log("item", item);
        }
    }, [item]);

    useEffect(() => {
        const loadCatalogos = async () => {
            setCargandoCatalogos(true);
            try {
                const [
                    responseTransportadora,
                    unidades,
                    responseGrupoGeocerca,
                    responseProducto,
                    responseTipoCarga,
                    responseUsuarioMovilTipoChofer,
                ] = await Promise.all([
                    obtenerListaTransportadora({ habilitado: 1 }, true, t),
                    obtenerTodasLasUnidades(),
                    obtenerListaGrupoGeocerca({ habilitado: 1 }, true, t),
                    obtenerListaProducto({ habilitado: 1 }, true, t),
                    obtenerListaTipoCarguio({ habilitado: 1 }, true, t),
                    obtenerUsuarioMovilTipoChofer(true),
                ]);

                if (Array.isArray(responseTransportadora)) {
                    setListaTransportadoras(responseTransportadora);
                }
                setListaVehiculos(Array.isArray(unidades) ? unidades : []);

                let idGrupoOrigen = null;
                let idGrupoDestino = null;
                let idGrupoGeoruta = null;
                if (Array.isArray(responseGrupoGeocerca)) {
                    idGrupoOrigen = responseGrupoGeocerca.find(
                        (g) => g.idConceptoGeocerca === 10
                    )?.idGrupoGeocerca;
                    idGrupoDestino = responseGrupoGeocerca.find(
                        (g) => g.idConceptoGeocerca === 2
                    )?.idGrupoGeocerca;
                    idGrupoGeoruta = responseGrupoGeocerca.find(
                        (g) => g.idConceptoGeocerca === 4
                    )?.idGrupoGeocerca;
                }

                const [geocercasOrigen, geocercasDestino, geocercasGeoruta] = await Promise.all([
                    obtenerGeocercasPorIdGrupoGeocerca({
                        habilitado: 1,
                        idGrupoGeoCerca: idGrupoOrigen,
                    }),
                    obtenerGeocercasPorIdGrupoGeocerca({
                        habilitado: 1,
                        idGrupoGeoCerca: idGrupoDestino,
                    }),
                    obtenerGeocercasPorIdGrupoGeocerca({
                        idGrupoGeoCerca: idGrupoGeoruta,
                    }),
                ]);

                if (
                    geocercasOrigen.error === false &&
                    Array.isArray(geocercasOrigen.body)
                ) {
                    setListaGeocercaOrigenes(geocercasOrigen.body);
                }
                if (
                    geocercasDestino.error === false &&
                    Array.isArray(geocercasDestino.body)
                ) {
                    setListaGeocercaDestinos(geocercasDestino.body);
                }
                if (
                    geocercasGeoruta.error === false &&
                    Array.isArray(geocercasGeoruta.body)
                ) {
                    setListaGeocercaGeoruta(geocercasGeoruta.body);
                }

                if (Array.isArray(responseProducto)) setListaProductos(responseProducto);
                if (Array.isArray(responseTipoCarga)) setListaTiposCarga(responseTipoCarga);
                if (Array.isArray(responseUsuarioMovilTipoChofer)) {
                    setListaChoferes(responseUsuarioMovilTipoChofer);
                }
            } catch (err) {
                console.log("DetalleRequerimientoViajeComponent loadCatalogos err", err);
            } finally {
                setCargandoCatalogos(false);
            }
        };

        loadCatalogos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!item) {
            setDynamicFields([]);
            setMetaFields([]);
            setTipoInputSelected(null);
            return;
        }
        const fields = parseRespuestas(item)
            .slice()
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        setDynamicFields(fields);
        setMetaFields(parseMeta(item));
        setTipoInputSelected(null);
        setMetaValidationVisible(false);
    }, [item?.id]);

    const handleDynamicFieldChange = (fieldId, value) => {
        setDynamicFields((prev) => {
            if (!Array.isArray(prev)) return prev;
            return prev.map((field) =>
                field.id === fieldId ? { ...field, value } : field
            );
        });
    };

    const isMetaLabelValid = (label) => {
        const text = String(label ?? "").trim();
        return text.length > 0 && META_LABEL_ALLOWED_REGEX.test(text);
    };

    const handleMetaLabelChange = (fieldId, value) => {
        const sanitized = String(value ?? "")
            .replace(META_LABEL_SANITIZE_REGEX, "")
            .slice(0, META_LABEL_MAX_LENGTH);
        handleMetaFieldChange(fieldId, "label", sanitized);
    };

    const isMetaNumeroValid = (value) =>
        sharedIsValidDecimalNumber(value, {
            maxLength: META_NUMERO_MAX_LENGTH,
            maxDecimals: META_NUMERO_MAX_DECIMALS,
            allowIncomplete: false,
        });

    const handleAgregarMetaField = () => {
        if (!tipoInputSelected?.value) return;
        setMetaFields((prev) => [
            ...(Array.isArray(prev) ? prev : []),
            {
                id: createMetaFieldId(),
                type: tipoInputSelected.value,
                label: "",
                value: "",
            },
        ]);
        setTipoInputSelected(null);
    };

    const handleMetaFieldChange = (fieldId, key, value) => {
        setMetaFields((prev) => {
            if (!Array.isArray(prev)) return prev;
            return prev.map((field) =>
                field.id === fieldId ? { ...field, [key]: value } : field
            );
        });
    };

    const handleMetaNumeroChange = (fieldId, value) => {
        if (
            !sharedIsValidDecimalNumber(value, {
                maxLength: META_NUMERO_MAX_LENGTH,
                maxDecimals: META_NUMERO_MAX_DECIMALS,
                allowIncomplete: true,
            })
        ) {
            return;
        }
        handleMetaFieldChange(fieldId, "value", value);
    };

    const handleEliminarMetaField = (fieldId) => {
        setMetaFields((prev) => {
            if (!Array.isArray(prev)) return prev;
            return prev.filter((field) => field.id !== fieldId);
        });
    };

    const handleAplicarTemplate = (camposTemplate) => {
        if (!Array.isArray(camposTemplate) || camposTemplate.length === 0) return;
        setMetaFields((prev) => {
            const actuales = Array.isArray(prev) ? prev : [];
            const flagsPresentes = new Set();
            actuales.forEach((f) => {
                FLAGS_SEMANTICOS_META.forEach((flag) => {
                    if (f?.[flag] === true) flagsPresentes.add(flag);
                });
            });
            const paraAgregar = camposTemplate.filter((c) => {
                const flagsDelCampo = FLAGS_SEMANTICOS_META.filter(
                    (flag) => c?.[flag] === true,
                );
                if (flagsDelCampo.length === 0) return true;
                return !flagsDelCampo.some((flag) => flagsPresentes.has(flag));
            });
            const nuevos = paraAgregar.map((c) => ({
                ...c,
                id: createMetaFieldId(),
                value: "",
            }));
            const omitidos = camposTemplate.length - nuevos.length;
            if (omitidos > 0) {
                showToast(
                    t("alert_campos_template_omitidos_duplicados") ||
                        `${omitidos} campo(s) del template omitido(s) por flag duplicado`,
                    "warning",
                    3500,
                );
            }
            return [...actuales, ...nuevos];
        });
    };

    const validarMetaFields = () => {
        if (!Array.isArray(metaFields) || metaFields.length === 0) {
            return true;
        }

        const hasInvalidLabel = metaFields.some((field) => !isMetaLabelValid(field.label));
        const hasInvalidNumero = metaFields.some(
            (field) =>
                field.type === TIPO_INPUT.NUMERO &&
                String(field.value ?? "").trim() !== "" &&
                !isMetaNumeroValid(field.value)
        );

        if (hasInvalidLabel || hasInvalidNumero) {
            setMetaValidationVisible(true);
            if (hasInvalidLabel) {
                showToast(t("alert_debe_ingresar_etiqueta"), "error", 3000);
            } else {
                showToast(t("alert_numero_invalido"), "error", 3000);
            }
            return false;
        }

        setMetaValidationVisible(false);
        return true;
    };

    const vehiculoSelectedValue = Array.isArray(dynamicFields)
        ? dynamicFields.find((f) => f.type === FORMULARIO_VIAJE_KEY.SELECCIONE_VEHICULO)?.value
        : null;

    useEffect(() => {
        const syncChoferFromVehiculo = async () => {
            if (!Array.isArray(dynamicFields) || dynamicFields.length === 0) return;

            const choferField = dynamicFields.find(
                (f) => f.type === FORMULARIO_VIAJE_KEY.SELECCIONE_CHOFER && f.enabled
            );
            if (!choferField) return;

            const valuePlaca = vehiculoSelectedValue?.value ?? null;
            if (valuePlaca == null || String(valuePlaca).trim() === "") {
                if (choferField.value) {
                    handleDynamicFieldChange(choferField.id, "");
                }
                return;
            }

            try {
                const response = await obtenerInfoConductorConNumeroPlaca({ valuePlaca });
                if (response.error === false && response.data) {
                    const conductor = response.data;
                    const choferOption =
                        listaChoferes.find((c) => c.value === conductor.id) ?? {
                            value: conductor.id,
                            label:
                                conductor.nombreConductor ??
                                conductor.label ??
                                String(conductor.id),
                        };
                    handleDynamicFieldChange(choferField.id, choferOption);
                }
                if (response.error === false && !response.data) {
                    handleDynamicFieldChange(choferField.id, null);
                }
            } catch (err) {
                console.log("DetalleRequerimientoViajeComponent syncChofer err", err);
            }
        };

        syncChoferFromVehiculo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehiculoSelectedValue?.value]);

    const googleFormInputClass =
        "w-full bg-transparent border-0 border-b border-gray-300 rounded-none px-0 py-2 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 focus:border-[color:var(--form-accent)] transition-colors";

    const googleFormSelectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: 44,
            border: "none",
            borderBottom: state.isFocused
                ? `2px solid ${COLOR_AZUL_MAIN}`
                : `1px solid ${FORM_BORDER}`,
            borderRadius: 0,
            boxShadow: "none",
            backgroundColor: "transparent",
            "&:hover": {
                borderBottom: state.isFocused
                    ? `2px solid ${COLOR_AZUL_MAIN}`
                    : `1px solid #5f6368`,
            },
        }),
        valueContainer: (base) => ({ ...base, padding: "4px 0" }),
        indicatorSeparator: () => ({ display: "none" }),
        placeholder: (base) => ({ ...base, color: "#9aa0a6" }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    };

    const wrapGoogleFormField = (field, control) => (
        <div
            key={field.id}
            className="group rounded-lg border bg-white shadow-sm transition-all focus-within:shadow-md"
            style={{
                borderColor: FORM_BORDER,
                ["--form-accent"]: COLOR_AZUL_MAIN,
            }}
        >
            <div
                className="h-1.5 rounded-t-lg opacity-0 transition-opacity group-focus-within:opacity-100"
                style={{ backgroundColor: COLOR_AZUL_MAIN }}
            />
            <div className="px-4 py-5 sm:px-6 sm:py-6">
                <label
                    htmlFor={field.key}
                    className="mb-4 block text-[16px] leading-6"
                    style={{ color: FORM_TEXT }}
                >
                    {field.label}
                    {field.obligatorio ? (
                        <span className="ml-1" style={{ color: FORM_REQUIRED }} aria-hidden="true">
                            *
                        </span>
                    ) : null}
                </label>
                <div className="w-full max-w-full sm:max-w-md">{control}</div>
            </div>
        </div>
    );

    const renderSelectField = (field, options, placeholder) =>
        wrapGoogleFormField(
            field,
            <Select
                inputId={field.key}
                options={options}
                value={field.value || null}
                onChange={(option) => handleDynamicFieldChange(field.id, option)}
                placeholder={placeholder}
                isClearable
                isSearchable
                styles={googleFormSelectStyles}
                classNamePrefix="google-form-select"
                className="text-black"
                menuPortalTarget={document.body}
            />
        );

    const renderDynamicField = (field) => {
        if (!field?.enabled) return null;

        switch (field.type) {
            case FORMULARIO_VIAJE_KEY.NOTA:
                return wrapGoogleFormField(
                    field,
                    <textarea
                        id={field.key}
                        name={field.key}
                        value={field.value ?? ""}
                        onChange={(e) => handleDynamicFieldChange(field.id, e.target.value)}
                        className={`${googleFormInputClass} resize-y min-h-[96px]`}
                        placeholder={t("placeholder_ingresa_nota")}
                        rows={3}
                        required={field.obligatorio}
                    />
                );
            case FORMULARIO_VIAJE_KEY.FECHA:
                return wrapGoogleFormField(
                    field,
                    <Calendar
                        id={field.key}
                        name={field.key}
                        value={field.value ?? ""}
                        onChange={(date) => handleDynamicFieldChange(field.id, date)}
                        placeholder={t("placeholder_seleccione_fecha")}
                        required={field.obligatorio}
                    />
                );
            case FORMULARIO_VIAJE_KEY.HORA:
                return wrapGoogleFormField(
                    field,
                    <TimeComponent
                        id={field.key}
                        name={field.key}
                        value={field.value ?? ""}
                        onChange={(time) => handleDynamicFieldChange(field.id, time)}
                        placeholder={t("placeholder_seleccione_hora")}
                        required={field.obligatorio}
                    />
                );
            case FORMULARIO_VIAJE_KEY.SELECCIONE_TRANSPORTADORA:
                return renderSelectField(
                    field,
                    listaTransportadoras,
                    t("placeholder_seleccione_transportadora")
                );
            case FORMULARIO_VIAJE_KEY.SELECCIONE_VEHICULO:
                return renderSelectField(
                    field,
                    listaVehiculos,
                    t("placeholder_seleccione_vehiculo")
                );
            case FORMULARIO_VIAJE_KEY.SELECCIONE_ORIGEN:
                return renderSelectField(
                    field,
                    listaGeocercaOrigenes,
                    t("placeholder_seleccione_geocerca_origen")
                );
            case FORMULARIO_VIAJE_KEY.SELECCIONE_DESTINO:
                return renderSelectField(
                    field,
                    listaGeocercaDestinos,
                    t("placeholder_seleccione_geocerca_destino")
                );
            case FORMULARIO_VIAJE_KEY.SELECCIONE_GEORUTA:
                return renderSelectField(
                    field,
                    listaGeocercaGeoruta,
                    t("placeholder_seleccione_geocerca_georuta")
                );
            case FORMULARIO_VIAJE_KEY.SELECCIONE_PRODUCTO:
                return renderSelectField(
                    field,
                    listaProductos,
                    t("placeholder_seleccione_producto")
                );
            case FORMULARIO_VIAJE_KEY.SELECCIONE_TIPO_DE_CARGA:
                return renderSelectField(
                    field,
                    listaTiposCarga,
                    t("placeholder_seleccione_tipo_de_carga")
                );
            case FORMULARIO_VIAJE_KEY.SELECCIONE_CHOFER:
                return renderSelectField(
                    field,
                    listaChoferes,
                    t("placeholder_seleccione_chofer")
                );
            case FORMULARIO_VIAJE_KEY.INPUT_CANTIDAD:
                return wrapGoogleFormField(
                    field,
                    <input
                        id={field.key}
                        name={field.key}
                        type="number"
                        value={field.value ?? ""}
                        onChange={(e) => handleDynamicFieldChange(field.id, e.target.value)}
                        className={googleFormInputClass}
                        placeholder={field.label}
                        required={field.obligatorio}
                        min={0}
                    />
                );
            default:
                return wrapGoogleFormField(
                    field,
                    <input
                        id={field.key}
                        name={field.key}
                        type="text"
                        value={field.value ?? ""}
                        onChange={(e) => handleDynamicFieldChange(field.id, e.target.value)}
                        className={googleFormInputClass}
                        placeholder={field.label}
                        required={field.obligatorio}
                    />
                );
        }
    };

    const handleActualizar = async () => {
        if (!item?.id) return;

        if (validarMetaFields() === false) {
            return;
        }

        const payload = {
            id: item.id,
            idFormularioViaje: item.idFormularioViaje ?? null,
            respuestas: dynamicFields,
            meta: metaFields,
        };

        showProcessing();
        const response = await actualizarProgramacionCamiones(payload);
        hideProcessing();

        if (response.error === false) {
            const updatedItem = {
                ...item,
                respuestas: JSON.stringify(dynamicFields),
                respuestasParsed: dynamicFields,
                meta: metaFields,
            };
            showToast(t("alert_se_actualizo_correctamente"), "success", 3000);
            if (typeof onSaved === "function") {
                onSaved(updatedItem);
            }
            return;
        }

        showToast(response.message || t("alert_no_se_pudo_crear"), "error", 3000);
    };

    const getTipoInputLabel = (type) =>
        listaTipoInput.find((option) => option.value === type)?.label ?? type;

    const renderMetaValueInput = (field) => {
        const showNumeroError =
            metaValidationVisible &&
            field.type === TIPO_INPUT.NUMERO &&
            String(field.value ?? "").trim() !== "" &&
            !isMetaNumeroValid(field.value);

        switch (field.type) {
            case TIPO_INPUT.NOTA:
                return (
                    <>
                        <textarea
                            value={field.value ?? ""}
                            onChange={(e) => {
                                if (e.target.value.length <= META_NOTA_MAX_LENGTH) {
                                    handleMetaFieldChange(field.id, "value", e.target.value);
                                }
                            }}
                            className={`${googleFormInputClass} resize-y min-h-[72px]`}
                            placeholder={t("placeholder_ingresa_nota")}
                            rows={3}
                            maxLength={META_NOTA_MAX_LENGTH}
                        />
                        <p className="text-xs text-gray-500 mt-1 text-right">
                            {String(field.value ?? "").length}/{META_NOTA_MAX_LENGTH}
                        </p>
                    </>
                );
            case TIPO_INPUT.FECHA:
                return (
                    <Calendar
                        id={`meta-fecha-${field.id}`}
                        name={`meta-fecha-${field.id}`}
                        value={field.value ?? ""}
                        onChange={(date) => handleMetaFieldChange(field.id, "value", date)}
                        placeholder={t("placeholder_seleccione_fecha")}
                    />
                );
            case TIPO_INPUT.FECHA_SALIDA:
                return (
                    <Calendar
                        id={`meta-fecha-salida-${field.id}`}
                        name={`meta-fecha-salida-${field.id}`}
                        value={field.value ?? ""}
                        onChange={(date) => handleMetaFieldChange(field.id, "value", date)}
                        placeholder={t("placeholder_seleccione_fecha")}
                    />
                );
            case TIPO_INPUT.FECHA_LLEGADA:
                return (
                    <Calendar
                        id={`meta-fecha-llegada-${field.id}`}
                        name={`meta-fecha-llegada-${field.id}`}
                        value={field.value ?? ""}
                        onChange={(date) => handleMetaFieldChange(field.id, "value", date)}
                        placeholder={t("placeholder_seleccione_fecha")}
                    />
                );
            case TIPO_INPUT.HORA:
                return (
                    <TimeComponent
                        id={`meta-hora-${field.id}`}
                        name={`meta-hora-${field.id}`}
                        value={field.value ?? ""}
                        onChange={(time) => handleMetaFieldChange(field.id, "value", time)}
                        placeholder={t("placeholder_seleccione_hora")}
                    />
                );
            case TIPO_INPUT.HORA_SALIDA:
                return (
                    <TimeComponent
                        id={`meta-hora-salida-${field.id}`}
                        name={`meta-hora-salida-${field.id}`}
                        value={field.value ?? ""}
                        onChange={(time) => handleMetaFieldChange(field.id, "value", time)}
                        placeholder={t("placeholder_seleccione_hora")}
                    />
                );
            case TIPO_INPUT.HORA_LLEGADA:
                return (
                    <TimeComponent
                        id={`meta-hora-llegada-${field.id}`}
                        name={`meta-hora-llegada-${field.id}`}
                        value={field.value ?? ""}
                        onChange={(time) => handleMetaFieldChange(field.id, "value", time)}
                        placeholder={t("placeholder_seleccione_hora")}
                    />
                );
            case TIPO_INPUT.GEOCERCA_ORIGEN:
                return (
                    <Select
                        inputId={`meta-geocerca-origen-${field.id}`}
                        options={listaGeocercaOrigenes}
                        value={field.value || null}
                        onChange={(option) => handleMetaFieldChange(field.id, "value", option)}
                        placeholder={t("placeholder_seleccione_geocerca_origen")}
                        isClearable
                        isSearchable
                        styles={googleFormSelectStyles}
                        classNamePrefix="google-form-select"
                        className="text-black"
                        menuPortalTarget={document.body}
                    />
                );
            case TIPO_INPUT.GEOCERCA_DESTINO:
                return (
                    <Select
                        inputId={`meta-geocerca-destino-${field.id}`}
                        options={listaGeocercaDestinos}
                        value={field.value || null}
                        onChange={(option) => handleMetaFieldChange(field.id, "value", option)}
                        placeholder={t("placeholder_seleccione_geocerca_destino")}
                        isClearable
                        isSearchable
                        styles={googleFormSelectStyles}
                        classNamePrefix="google-form-select"
                        className="text-black"
                        menuPortalTarget={document.body}
                    />
                );
            case TIPO_INPUT.GEORUTA:
                return (
                    <Select
                        inputId={`meta-georuta-${field.id}`}
                        options={listaGeocercaGeoruta}
                        value={field.value || null}
                        onChange={(option) => handleMetaFieldChange(field.id, "value", option)}
                        placeholder={t("placeholder_seleccione_geocerca_georuta")}
                        isClearable
                        isSearchable
                        styles={googleFormSelectStyles}
                        classNamePrefix="google-form-select"
                        className="text-black"
                        menuPortalTarget={document.body}
                    />
                );
            case TIPO_INPUT.SELECCIONE_VEHICULO:
                return (
                    <Select
                        inputId={`meta-vehiculo-${field.id}`}
                        options={listaVehiculos}
                        value={field.value || null}
                        onChange={(option) => handleMetaFieldChange(field.id, "value", option)}
                        placeholder={t("placeholder_seleccione_vehiculo")}
                        isClearable
                        isSearchable
                        styles={googleFormSelectStyles}
                        classNamePrefix="google-form-select"
                        className="text-black"
                        menuPortalTarget={document.body}
                    />
                );
            case TIPO_INPUT.NUMERO:
                return (
                    <>
                        <input
                            type="text"
                            inputMode="decimal"
                            value={field.value ?? ""}
                            onChange={(e) => handleMetaNumeroChange(field.id, e.target.value)}
                            className={googleFormInputClass}
                            placeholder={t("label_valor")}
                            maxLength={META_NUMERO_MAX_LENGTH}
                        />
                        {showNumeroError ? (
                            <p className="text-xs mt-1" style={{ color: FORM_REQUIRED }}>
                                {t("alert_numero_invalido")}
                            </p>
                        ) : null}
                    </>
                );
            case TIPO_INPUT.TEXTO:
            default:
                return (
                    <>
                        <input
                            type="text"
                            value={field.value ?? ""}
                            onChange={(e) => {
                                if (e.target.value.length <= META_TEXTO_MAX_LENGTH) {
                                    handleMetaFieldChange(field.id, "value", e.target.value);
                                }
                            }}
                            className={googleFormInputClass}
                            placeholder={t("label_valor")}
                            maxLength={META_TEXTO_MAX_LENGTH}
                        />
                        <p className="text-xs text-gray-500 mt-1 text-right">
                            {String(field.value ?? "").length}/{META_TEXTO_MAX_LENGTH}
                        </p>
                    </>
                );
        }
    };

    if (!item) {
        return (
            <div className="h-full min-h-[300px] flex items-center justify-center text-sm text-gray-400">
                {t("label_sin_datos")}
            </div>
        );
    }

    if (cargandoCatalogos) {
        return cargandoCard();
    }

    return (
        <>
            <div
                className="rounded-lg p-3 sm:p-4"
                style={{ backgroundColor: FORM_BG }}
            >
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                        <h3 className="text-lg font-semibold" style={{ color: FORM_TEXT }}>
                            {item.formularioViaje || t("label_solicitud")}
                        </h3>
                        {item.fechaProceso ? (
                            <p className="text-sm text-gray-500 mt-1">{item.fechaProceso}</p>
                        ) : null}
                    </div>
                    <button
                        type="button"
                        className={ESTILO_BOTON_MODAL_ACEPTAR}
                        onClick={handleActualizar}
                    >
                        {t("label_actualizar")}
                    </button>
                </div>

                <div className="space-y-4">
                    {Array.isArray(dynamicFields) && dynamicFields.length > 0 ? (
                        dynamicFields.map((field) => renderDynamicField(field))
                    ) : (
                        <div className="text-sm text-gray-500 bg-white border border-dashed border-gray-300 rounded-lg p-6 text-center">
                            {t("label_sin_datos")}
                        </div>
                    )}
                </div>

            </div>

            <div
                className="rounded-lg mt-4 p-3 sm:p-4"
                style={{ backgroundColor: FORM_BG }}
            >
                <div className="mb-4">
                    <MetaTemplatePicker
                        currentCampos={metaFields}
                        onApply={handleAplicarTemplate}
                        onBeforeSave={validarMetaFields}
                    />
                </div>
                <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
                    <div className="flex-1 min-w-0">
                        <label
                            className="mb-2 block text-sm font-medium"
                            style={{ color: FORM_TEXT }}
                        >
                            {t("label_tipo")}
                        </label>
                        <Select
                            options={listaTipoInput}
                            value={tipoInputSelected}
                            onChange={setTipoInputSelected}
                            placeholder={t("placeholder_seleccione_tipo")}
                            isClearable
                            isSearchable
                            styles={googleFormSelectStyles}
                            classNamePrefix="google-form-select"
                            className="text-black"
                            menuPortalTarget={document.body}
                        />
                    </div>
                    <button
                        type="button"
                        className={`${ESTILO_BOTON_MODAL_ACEPTAR} inline-flex items-center gap-2 shrink-0`}
                        onClick={handleAgregarMetaField}
                        disabled={!tipoInputSelected}
                    >
                        <Plus className="h-4 w-4" />
                        {t("label_agregar")}
                    </button>
                </div>

                <div className="space-y-3">
                    {Array.isArray(metaFields) && metaFields.length > 0 ? (
                        metaFields.map((field) => (
                            <div
                                key={field.id}
                                className="rounded-lg border bg-white p-4 shadow-sm"
                                style={{ borderColor: FORM_BORDER }}
                            >
                                <div className="flex items-start justify-between gap-3 mb-3">
                                    <span
                                        className="text-xs font-semibold uppercase tracking-wide"
                                        style={{ color: FORM_TEXT }}
                                    >
                                        {getTipoInputLabel(field.type)}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => handleEliminarMetaField(field.id)}
                                        className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                                        title={t("label_eliminar")}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label
                                            className="mb-2 block text-sm"
                                            style={{ color: FORM_TEXT }}
                                        >
                                            {t("label_etiqueta")}
                                            <span className="ml-1" style={{ color: FORM_REQUIRED }}>
                                                *
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            value={field.label ?? ""}
                                            onChange={(e) =>
                                                handleMetaLabelChange(field.id, e.target.value)
                                            }
                                            className={googleFormInputClass}
                                            placeholder={t("placeholder_ingresa_etiqueta")}
                                            maxLength={META_LABEL_MAX_LENGTH}
                                        />
                                        <p className="text-xs text-gray-500 mt-1 text-right">
                                            {String(field.label ?? "").length}/{META_LABEL_MAX_LENGTH}
                                        </p>
                                        {metaValidationVisible && !isMetaLabelValid(field.label) ? (
                                            <p className="text-xs mt-1" style={{ color: FORM_REQUIRED }}>
                                                {t("alert_debe_ingresar_etiqueta")}
                                            </p>
                                        ) : null}
                                    </div>
                                    <div>
                                        <label
                                            className="mb-2 block text-sm"
                                            style={{ color: FORM_TEXT }}
                                        >
                                            {t("label_valor")}
                                        </label>
                                        {renderMetaValueInput(field)}
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-sm text-gray-500 bg-white border border-dashed border-gray-300 rounded-lg p-6 text-center">
                            {t("label_sin_datos")}
                        </div>
                    )}
                </div>
            </div>

            <div
                className="rounded-lg mt-4 p-3 sm:p-4"
                style={{ backgroundColor: FORM_BG }}
            >
                <div className="flex justify-end mt-4">
                    <button
                        type="button"
                        className={ESTILO_BOTON_MODAL_ACEPTAR}
                        onClick={handleActualizar}
                    >
                        {t("label_actualizar")}
                    </button>
                </div>
            </div>
        </>
    );
}
